import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import "../App.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ?? "https://psucta.com";

const PAGE_SIZE = 10;
const MAX_ITEMS_PER_CLASS = 50;
const CACHE_KEY = "stats_run_count_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // treat cached data as fresh for 5 minutes

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { timestamp, data } = JSON.parse(raw);
    if (!Array.isArray(data) || Date.now() - timestamp > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {
    // localStorage unavailable (e.g. private browsing) — safe to ignore
  }
}

const CLASS_ORDER = ["FM", "GM", "MF", "FF", "FG", "FT", "AM", "AF", "AT", "FiG", "GT", "WT", "PT"]; // optional: your preferred order

function PaginatedClassList({ title, items }) {
  const [page, setPage] = useState(0);

  const pageCount = Math.ceil(items.length / PAGE_SIZE);

  const visible = useMemo(() => {
    const start = page * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  // keep page valid if items length changes
  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(pageCount - 1, 0)));
  }, [pageCount]);

  if (items.length === 0) return null;
  const startNumber = page * PAGE_SIZE + 1;
  return (
    <section className="stats-list">
      <h3 style={{ marginBottom: 8 }}>{title}</h3>

      <ol className="list" start={startNumber}>
        {visible.map((p) => (
          <li key={`${p.class_code}-${p.id}`}>
            <Link to={`/players/${p.id}`}>{p.player_name}</Link>: {p.run_count}
          </li>
        ))}
      </ol>

      <div className="pager">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={page === 0}
        >
          Prev
        </button>

        <span>
          Page {page + 1} / {Math.max(pageCount, 1)}
        </span>

        <button
          onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
          disabled={page >= pageCount - 1}
        >
          Next
        </button>
      </div>
    </section>
  );
}

export default function Stats() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Paint instantly from cache while revalidating in the background
      const cached = readCache();
      if (cached) {
        setRows(cached);
        setLoading(false);
      }

      try {
        if (!cached) setLoading(true);
        setError(null);

        const res = await fetch(`${API_BASE}/api/stats/run_count`);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);

        const data = await res.json();
        const parsed = Array.isArray(data) ? data : [];
        if (!cancelled) {
          setRows(parsed);
          writeCache(parsed);
        }
      } catch (e) {
        if (!cancelled && !cached) setError(e.message || "Failed to load stats");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group rows by class_code, and limit each class to top 15
  const grouped = useMemo(() => {
    // Optional: sort within each class by run_count desc (if API doesn't already)
    // If run_count is string, coerce with Number(...)
    const byClass = new Map();

    for (const r of rows) {
      const code = r.class_code ?? "Unknown";
      if (!byClass.has(code)) byClass.set(code, []);
      byClass.get(code).push(r);
    }

    // Sort each group, then slice to MAX_ITEMS_PER_CLASS
    for (const [code, list] of byClass.entries()) {
      list.sort((a, b) => Number(b.run_count) - Number(a.run_count));
      byClass.set(code, list.slice(0, MAX_ITEMS_PER_CLASS));
    }

    return byClass;
  }, [rows]);

  // Decide display order (your preferred order first, then any extras)
  const classCodes = useMemo(() => {
    const codes = Array.from(grouped.keys());
    const preferred = CLASS_ORDER.filter((c) => codes.includes(c));
    const extras = codes
      .filter((c) => !CLASS_ORDER.includes(c))
      .sort((a, b) => a.localeCompare(b));
    return [...preferred, ...extras];
  }, [grouped]);

  if (loading) {
    return (
      <div className="App">
        <div className="statsTotals">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="statsTotals">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="App statsPage">
      <div className="statsTotals">
        {classCodes.map((code) => (
          <PaginatedClassList
            key={code}
            title={`${code}`}
            items={grouped.get(code) ?? []}
          />
        ))}
      </div>
    </div>
  );
}
